/**
 * Health-plan block rules for Create Claim — a VERBATIM port of Provider
 * Portal's `checkMemberAccess` (providerPortalFrontend
 * `frontend/src/components/Claims/CreateClaimModal/index.tsx:309-343`).
 *
 * These are contractual routing rules, not UX niceties: for these health plans
 * Astrana does not adjudicate the claim, and the provider must send it to the
 * payer directly. Legacy blocks the hand-off to the claim form when one matches
 * and shows the message instead. The message TEXT is part of the contract
 * (providers are given a physical mailing address) and is reproduced character
 * for character — do not reword it.
 *
 * The one deliberate substitution: legacy reads the ACTING IPA from
 * `sessionStorage.getItem("ipaGroup")`, which does not exist in this host. The
 * caller passes the acting IPA it already has instead (see the gate's doc for
 * which value that is and why).
 */

/** Legacy `Company.NCPN`. */
export const NCPN_COMPANY_ID = "NCPN";

/**
 * Legacy `ncpnBlockCodes` — Caremore plans NCPN's contract routes directly to
 * Caremore Health. The `name` on each entry is carried for traceability only;
 * legacy never renders it (it matches on `hpcode` alone).
 */
export const NCPN_CAREMORE_BLOCK_CODES: ReadonlyArray<{ hpCode: string; name: string }> = [
  { hpCode: "CRCAMC", name: "CAREMORE ANTHEM BLUE CROSS MEDICONNECT" },
  { hpCode: "CRCALS", name: "CAREMORE ANTHEM BLUE CROSS SENIOR" },
  { hpCode: "CRSCAN", name: "CAREMORE SCAN SENIOR" },
];

export const NCPN_CAREMORE_MESSAGE =
  "As part of NCPN's contract with Caremore Health, please submit claims to: Caremore Health Attn: HMO Claims Dept PO Box 366 Artesia, CA 90702-0366";

export const HNCC_MESSAGE =
  "Health Net Covered California member claims should be forwarded to Health Net directly: Health Net Commercial Claims, P.O. Box 9040, Farmington, MO 63640-9040";

export const BSPP_MESSAGE =
  "Patient is a Blue Shield PPO member - please forward claim to Blue Shield directly";

/**
 * Returns the blocking message(s) for this member's health plan, or an empty
 * array when the claim may proceed. Same short-circuit order as legacy (the
 * first matching rule returns alone).
 *
 * @param hpCode    The member's health plan code (PP's `RmiHpCode`). NOT the
 *                  IPA/tenant code — see members/toClaimContext.ts.
 * @param actingIpa The acting user's IPA (legacy's `sessionStorage.ipaGroup`).
 */
export function checkClaimMemberAccess(
  hpCode: string | null | undefined,
  actingIpa: string | null | undefined,
): string[] {
  const hp = (hpCode ?? "").trim().toUpperCase();
  const ipa = (actingIpa ?? "").trim().toUpperCase();
  if (!hp) return [];

  if (
    NCPN_CAREMORE_BLOCK_CODES.some((e) => e.hpCode === hp) &&
    ipa === NCPN_COMPANY_ID
  ) {
    return [NCPN_CAREMORE_MESSAGE];
  }

  if (hp === "HNCC") return [HNCC_MESSAGE];

  if (hp === "BSPP") return [BSPP_MESSAGE];

  return [];
}
